import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { DateTime } from 'luxon';
import Swal from 'sweetalert2';

let now = DateTime.now();
let CURRENT_TIME = now.toFormat('hh:mm a');
let CURRENT_DATE = now.toFormat('yyyy-MM-dd');
const currentYear = DateTime.now().toFormat('yyyy');

export interface ExtendedWalletsOption {
  applePay?: 'auto' | 'never';
  googlePay?: 'auto' | 'never';
  link?: 'auto' | 'never'; // custom addition
  cashAppPay?: 'auto' | 'never'; // custom addition
}
export const TIMEZONE_ABBREVIATIONS: { [key: string]: string } = {
  'America/Los_Angeles': 'PST',
  'America/Denver': 'MST',
  'America/Chicago': 'CST',
  'America/New_York': 'EST',
  'Asia/Kolkata': 'IST',
};

export function getTournamentRegistrationMetadata(timezone: string = 'UTC') {
  const now = DateTime.now().setZone(timezone);
  return {
    registrationDate: now.toFormat('yyyy-MM-dd'),
    registrationTime: now.toFormat('hh:mm a'),
    timeZone: timezone,
  };
}

// 'pi_123_secret_abc' -> 'pi_123'
export function getIntentIdFromClientSecret(clientSecret?: string): string {
  return (clientSecret ?? '').split('_secret_')[0];
}

export function getEventRegistrationMetadata(timezone: string = 'UTC') {
  const now = DateTime.now().setZone(timezone);
  return {
    date: now.toFormat('yyyy-MM-dd'),
    time: now.toFormat('hh:mm a'),
    timeZone: timezone,
  };
}

export function isEventPast(
  eventDate: string,
  eventTime: string,
  eventTimezone: string = 'UTC',
): boolean {
  try {
    // Try parsing with 24-hour format first
    let eventDateTime = DateTime.fromFormat(
      `${eventDate} ${eventTime}`,
      'yyyy-MM-dd HH:mm',
      { zone: eventTimezone },
    );

    // If that fails, try 12-hour format
    if (!eventDateTime.isValid) {
      eventDateTime = DateTime.fromFormat(
        `${eventDate} ${eventTime}`,
        'yyyy-MM-dd h:mm a',
        { zone: eventTimezone },
      );
    }

    // If still invalid, try with just the date (for all-day events)
    if (!eventDateTime.isValid) {
      const eventDayEnd = DateTime.fromFormat(eventDate, 'yyyy-MM-dd', {
        zone: eventTimezone,
      }).endOf('day');
      const nowInEventZone = DateTime.now().setZone(eventTimezone);
      return nowInEventZone > eventDayEnd;
    }

    const nowInEventZone = DateTime.now().setZone(eventTimezone);
    return nowInEventZone > eventDateTime;
  } catch (error) {
    // Fallback to local time comparison if parsing fails
    const eventEnd = new Date(eventDate + 'T' + eventTime);
    return new Date() > eventEnd;
  }
}

export function isEventRunning(
  eventDate: string,
  eventStartTime: string,
  eventEndTime: string,
  eventTimezone: string = 'UTC',
): boolean {
  try {
    // Try parsing with 24-hour format first
    let eventStart = DateTime.fromFormat(
      `${eventDate} ${eventStartTime}`,
      'yyyy-MM-dd HH:mm',
      { zone: eventTimezone },
    );

    // If that fails, try 12-hour format
    if (!eventStart.isValid) {
      eventStart = DateTime.fromFormat(
        `${eventDate} ${eventStartTime}`,
        'yyyy-MM-dd h:mm a',
        { zone: eventTimezone },
      );
    }

    let eventEnd = DateTime.fromFormat(
      `${eventDate} ${eventEndTime}`,
      'yyyy-MM-dd HH:mm',
      { zone: eventTimezone },
    );

    if (!eventEnd.isValid) {
      eventEnd = DateTime.fromFormat(
        `${eventDate} ${eventEndTime}`,
        'yyyy-MM-dd h:mm a',
        { zone: eventTimezone },
      );
    }

    if (!eventStart.isValid || !eventEnd.isValid) {
      return false;
    }

    const nowInEventZone = DateTime.now().setZone(eventTimezone);
    return nowInEventZone >= eventStart && nowInEventZone <= eventEnd;
  } catch (error) {
    return false;
  }
}

export function isTournamentPast(
  eventDate: string,
  eventTimezone: string = 'UTC',
): boolean {
  try {
    const eventDayStart = DateTime.fromFormat(eventDate, 'yyyy-MM-dd', {
      zone: eventTimezone,
    }).endOf('day');
    const nowInEventZone = DateTime.now().setZone(eventTimezone);
    return nowInEventZone > eventDayStart;
  } catch (error) {
    // Fallback
    return new Date() > new Date(eventDate + 'T23:59:59');
  }
}

function renderFontFamily(headlineFont: string): string {
  if (headlineFont === 'Poppins') {
    return 'poppins-font';
  } else if (headlineFont === 'Roboto') {
    return 'roboto-font';
  } else if (headlineFont === 'Gilroy') {
    return 'gilroy-font';
  } else if (headlineFont === 'Chandler Mountain') {
    return 'chandler-font';
  } else {
    return 'paladins-font';
  }
}

function setVideoThumbnailImage(url: string): string {
  if (url) {
    return url;
  } else {
    return 'assets/img/video-poster-img.jpg';
  }
}
function trimSpacesFromCenter(str: string) {
  return str?.replace(/\s+/g, ' ')?.trim();
}

function getSubdomainOrDomain(hostname: string): string {
  // let domains = ['localhost', 'sketchplaydev.com', 'sketchplayqa.com', 'sketchplaypreprod.com', 'sketchplaydemo.com', 'sketchplay.io'];
  // const isSpecialDomain = domains.some(domain => hostname.includes(domain));
  // if (isSpecialDomain) {
  //   return hostname.split('.')[0];
  // }
  return hostname;
}
const showAlertMessage = (
  text: string,
  type: 'success' | 'error' | 'warning',
  onCloseCallback?: () => void,
  titleText?: string,
) => {
  let imageUrl: string;
  let textBelowIcon: string;
  let imageStyle: string = '';

  if (type === 'success') {
    imageUrl = 'assets/img/success@2x.png';
    textBelowIcon = 'Tap anywhere to continue.';
  } else if (type === 'warning') {
    imageUrl = 'assets/img/info.png';
    textBelowIcon = 'Tap anywhere to continue.';
    // Apply CSS filter to make the icon orange/yellow for warning
    imageStyle =
      'filter: brightness(0) saturate(100%) invert(60%) sepia(98%) saturate(1735%) hue-rotate(360deg) brightness(102%) contrast(101%);';
  } else {
    imageUrl = 'assets/img/info.png';
    textBelowIcon = 'Tap anywhere to try again.';
  }

  Swal.fire({
    title: '',
    html: `
      <div style="text-align: center; ">
        <div style="margin-top : 40px; color: #3B3B3B; font-weight: 800; font-size: 20px; font-family: 'Poppins', sans-serif;"> ${text}</div>
        <div style="display: flex; justify-content: center; margin: 20px 0px 20px 0px"><img style="height: 74px; width: 74px; ${imageStyle}" src="${imageUrl}" alt="alert"/></div>
        <div style=""><p style="font-size : 14px; color: #3B3B3B; font-family: 'Arial', sans-serif;"> ${textBelowIcon} </p> </div>
      </div>
    `,
    icon: undefined,
    showConfirmButton: false,
    animation: false,
    buttonsStyling: false,
    showCloseButton: false,
    allowOutsideClick: true,
    customClass: {
      popup: 'custom-popup-class',
      container: 'custom-container-class',
    },
    didOpen: (popup) => {
      popup.addEventListener('click', () => {
        Swal.clickConfirm();
      });
    },
    didClose: onCloseCallback,
  });
};

function formatStringAmountToDecimal(value: number): string {
  // Check if the number is an integer
  if (Number.isInteger(value)) {
    // Return the value with ".00"
    return `${value}.00`;
  } else {
    // Return the value as a string with no modification
    return value?.toFixed(2);
  }
}

function formatCurrency(amount: number = 0, currency?: string) {
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency ? currency : 'USD',
  });
  return formatter.format(amount);
}

function socialUrlValidator(domains: string | string[]): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (!value) return null;

    const domainList = Array.isArray(domains) ? domains : [domains];
    const escapedDomains = domainList
      .map((domain) => domain.replace(/\./g, '\\.'))
      .join('|');
    // Deliberate allow-list, not RFC 3986: excludes [](){}<>"'` etc. so a
    // pasted markdown/HTML link (e.g. "[text](url)") can't sneak past as a
    // single non-whitespace token while still accepting @handles and query strings.
    const pattern = new RegExp(
      `^(https?:\\/\\/)?(www\\.)?(${escapedDomains})\\/[A-Za-z0-9\\-._~%\\/?=&@]+\\/?$`,
      'i',
    );

    return pattern.test(value.trim()) ? null : { invalidUrl: true };
  };
}

function noLeadingOrTrailingSpacesValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    const isWhitespace =
      String(value === null || value === undefined ? '' : value).trim()
        .length === 0;
    const isValid = !isWhitespace;
    return isValid ? null : { whitespace: true };
  };
}

function formatDateCommon(
  date: string,
  actuallFormat: string,
  givenFormat?: string,
) {
  if (!actuallFormat || typeof actuallFormat !== 'string') {
    return ''; // Return an empty string if the format is not provided
  }
  // Moment tokens: YYYY-MM-DD -> Luxon: yyyy-MM-dd
  const targetFormat = (givenFormat || 'yyyy-MM-dd')
    .replace(/YYYY/g, 'yyyy')
    .replace(/DD/g, 'dd');
  const sourceFormat = actuallFormat
    .replace(/YYYY/g, 'yyyy')
    .replace(/DD/g, 'dd');

  return DateTime.fromFormat(date, sourceFormat, { zone: 'utc' }).toFormat(
    targetFormat,
  );
}

function parseDateWithoutTimezone(date: string | Date, format?: string) {
  if (!date) {
    return ''; // Return an empty string if the date is not provided
  }
  const targetFormat = (format || 'yyyy-MM-dd')
    .replace(/YYYY/g, 'yyyy')
    .replace(/DD/g, 'dd');
  if (typeof date === 'string') {
    const isoDate = DateTime.fromISO(date);
    if (isoDate.isValid) {
      return isoDate.toFormat(targetFormat);
    }
    return DateTime.fromFormat(date, 'MM/dd/yyyy').toFormat(targetFormat);
  } else {
    return DateTime.fromJSDate(date).toFormat(targetFormat);
  }
}

function getOrdinalSuffix(day: number): string {
  if (day > 3 && day < 21) return 'th';
  switch (day % 10) {
    case 1:
      return 'st';
    case 2:
      return 'nd';
    case 3:
      return 'rd';
    default:
      return 'th';
  }
}

function formatWithOrdinal(date: DateTime, formatStr: string): string {
  // Luxon doesn't support 'Do' for ordinal days.
  // We'll replace 'Do' in the format string with the day and its suffix.
  if (formatStr.includes('Do')) {
    const suffix = getOrdinalSuffix(date.day);
    return date.toFormat(formatStr.replace(/Do/g, `d'${suffix}'`));
  }
  return date.toFormat(formatStr);
}

// function noWhitespaceValidator(control: AbstractControl): ValidationErrors | null {
//   const isWhitespace = (control.value || '').trim().length === 0;
//   return isWhitespace ? { whitespace: true } : null;
// }

function noWhitespaceValidator(
  control: AbstractControl,
): ValidationErrors | null {
  const value = control.value;

  if (typeof value === 'string') {
    if (!value.trim()) {
      return { whitespace: true };
    }
  }

  // If it's not a string (e.g., boolean from checkbox), no whitespace validation needed
  return null;
}

function getFileName(url: string) {
  const match = url?.match(/\/([^\/?#]+)[^\/]*$/);
  return match ? match[1] : '';
}

function getFileNameFromUrl(url: string): string {
  return url.split('/').pop()?.split('?')[0] || 'download';
}

type FieldType =
  | 'text'
  | 'dropdown'
  | 'radio'
  | 'toggle'
  | 'file'
  | 'textarea'
  | 'checkbox'
  | 'datepicker'
  | 'timepicker'
  | 'sectionHeading';
type fileType =
  | 'image'
  | 'video'
  | 'pdf'
  | 'doc'
  | 'docx'
  | 'csv'
  | 'txt'
  | 'ppt';

interface CustomField {
  id: string;
  type: FieldType;
  label: string;
  placeholder: string;
  required: boolean;
  name: string;
  value: any;
  showOptions?: boolean;
  multiple?: boolean; // dropdown: allow selecting multiple options
  options?: any[];
  _formatKey?: number;
  flatpickrMode?: string;
  mode?: 'single' | 'multiple' | 'range';
  dateFormat?: string; // (for date & time)
  // fileType?: 'image' | 'video' | 'pdf' | 'doc' | 'docx' | 'csv' | 'txt' | 'ppt';
  fileType?: string[];
  inputType?: 'text' | 'number' | 'email' | 'tel';
  animate?: boolean;
}

function toCamelCase(str: string) {
  return str
    .trim() // Remove leading and trailing whitespace
    .replace(/\s+/g, ' ') // Collapse multiple spaces into one
    .replace(/[^a-zA-Z0-9 ]/g, '') // Remove non-alphanumeric characters except space
    .split(' ')
    .map((word, index) => {
      if (index === 0) return word.toLowerCase();
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join('');
}

function isEmptyObject(obj: any) {
  return obj && Object.keys(obj).length === 0 && obj.constructor === Object;
}

function charValidate(text: string, limit: number): string {
  if (text !== undefined && limit !== undefined) {
    if (text !== null) {
      if (text.length > limit) {
        return `${text.substring(0, limit - 3)}...`;
      } else {
        return `${text.substring(0, limit)}`;
      }
    } else {
      return '';
    }
  } else {
    return '';
  }
}
// Very simple map: timezone string → offset in minutes from UTC
// ➜ Add/adjust entries based on what your backend sends in `tournamentData.timezone`
const TIMEZONE_OFFSETS: { [key: string]: number } = {
  UTC: 0,
  'America/New_York': -300, // UTC-5
  'America/Chicago': -360, // UTC-6
  'America/Denver': -420, // UTC-7
  'America/Los_Angeles': -480, // UTC-8
  'Europe/London': 0, // UTC+0
  'Europe/Paris': 60, // UTC+1
  'Asia/Kolkata': 330, // UTC+5:30
  // add more as needed to match your system
};

export function getTimezoneOffsetMinutes(
  tz: string | null | undefined,
): number {
  if (!tz) return 0; // default UTC
  return TIMEZONE_OFFSETS[tz] ?? 0; // fallback to 0 if unknown
}

function removeDuplicates(array: any[], key: string) {
  return array.filter((item, index) => {
    // Return the first occurrence of each object based on the specified key
    return index === array.findIndex((obj) => obj[key] === item[key]);
  });
}

function getResolution() {
  return {
    screen: {
      width: window.screen.width,
      height: window.screen.height,
    },
    viewport: {
      width: window.innerWidth,
      height: window.innerHeight,
    },
  };
}
function formatTimeTZRange(
  startTime: string,
  endTime: string,
  timezone?: string,
) {
  if (!startTime || !endTime) return '';

  const formatSingleTime = (
    time: string,
    tz?: string,
    showTZ: boolean = false,
  ) => {
    let dt: DateTime = DateTime.invalid('initial');
    const zone = tz || 'local';

    if (typeof time === 'string') {
      const upperTime = time.trim().toUpperCase();

      // 1. Try 12-hour formats (h:mm a, h:mm:ss a)
      if (upperTime.includes('AM') || upperTime.includes('PM')) {
        dt = DateTime.fromFormat(
          upperTime,
          upperTime.includes(':') && upperTime.split(':').length === 3
            ? 'h:mm:ss a'
            : 'h:mm a',
          { zone },
        );
      }
      // 2. Try 24-hour formats (HH:mm:ss, HH:mm)
      if (!dt.isValid && upperTime.match(/^\d{1,2}:\d{2}(:\d{2})?$/)) {
        dt = DateTime.fromFormat(
          upperTime,
          upperTime.includes(':') && upperTime.split(':').length === 3
            ? 'HH:mm:ss'
            : 'HH:mm',
          { zone },
        );
      }

      // 3. Fallback to ISO
      if (!dt.isValid) {
        dt = DateTime.fromISO(time, { zone });
      }

      // 4. Fallback to JS Date
      if (!dt.isValid) {
        const jsDate = new Date(time);
        if (!isNaN(jsDate.getTime())) {
          dt = DateTime.fromJSDate(jsDate).setZone(zone);
        }
      }
    } else {
      dt = DateTime.fromJSDate(new Date(time)).setZone(zone);
    }

    if (!dt.isValid) return '';

    // Always show minutes (hh:mm), Day period (a) - using toUpperCase to ensure AM/PM
    const timeStr = dt.toFormat('hh:mm a').toUpperCase();
    let zoneStr = '';
    if (showTZ) {
      const timezoneKey = (tz || dt.zoneName || '').trim();
      let abbreviation = TIMEZONE_ABBREVIATIONS[timezoneKey];

      // If DST applies to this date, update the static standard time (ST) abbreviation to daylight time (DT)
      if (abbreviation && dt.isInDST) {
        if (abbreviation === 'GMT') {
          abbreviation = 'BST';
        } else if (abbreviation === 'CET') {
          abbreviation = 'CEST';
        } else if (abbreviation.includes('ST')) {
          abbreviation = abbreviation.replace('ST', 'DT');
        }
      }

      if (!abbreviation) {
        // Robust fallback logic (re-used from formatDateWithCurrentTimezone)
        const luxonAbbr = dt.offsetNameShort;
        if (
          luxonAbbr &&
          !luxonAbbr.includes('GMT') &&
          !luxonAbbr.includes('UTC') &&
          isNaN(parseInt(luxonAbbr.charAt(0)))
        ) {
          abbreviation = luxonAbbr;
        } else {
          abbreviation = dt.toFormat('ZZZZ').replace('GMT', 'UTC');
        }
      }
      zoneStr = ` ${abbreviation}`;
    }

    return `${timeStr}${zoneStr}`;
  };

  const start = formatSingleTime(startTime, timezone);
  const end = formatSingleTime(endTime, timezone, true);

  return start && end ? `${start} - ${end}` : start || end || '';
}
function getReturnUrl(): string {
  const returnUrl = new URL(window.location.href);
  returnUrl.searchParams.delete('setup_intent');
  returnUrl.searchParams.delete('setup_intent_client_secret');
  returnUrl.searchParams.delete('payment_intent');
  returnUrl.searchParams.delete('payment_intent_client_secret');
  returnUrl.searchParams.delete('redirect_status');
  return returnUrl.toString();
}

export interface ImportErrorItem {
  sheet?: string;
  row?: number;
  reason?: string;
  message?: string;
}

const escapeHtml = (unsafe: string) => {
  return (unsafe || '').replace(/[&<"'>]/g, function (match) {
    switch (match) {
      case '&':
        return '&amp;';
      case '<':
        return '&lt;';
      case '>':
        return '&gt;';
      case '"':
        return '&quot;';
      case "'":
        return '&#039;';
      default:
        return match;
    }
  });
};

/**
 * Builds HTML list items for import errors, formatting row information if present.
 * Sheet information is excluded so that the location starts from the row.
 *
 * @param errors - An array of import error items.
 * @returns HTML string representation of the error list.
 */
const buildImportErrorListHtml = (errors: ImportErrorItem[]): string =>
  (errors || [])
    .map((err) => {
      const location = err.row ? `Row ${err.row}` : '';
      const prefix = location
        ? `<strong>${escapeHtml(location)}</strong>: `
        : '';
      return `<li style="margin-bottom: 8px; font-size: 15px; line-height: 1.4; color: #333;">${prefix}${escapeHtml(err.reason ?? err.message ?? '')}</li>`;
    })
    .join('');

/**
 * Displays a SweetAlert2 popup showing a list of import errors.
 *
 * @param message - The main error message header.
 * @param errors - List of error items to display.
 * @param onCloseCallback - Callback triggered when the alert is closed.
 */
const showImportErrorsAlertV2 = (
  message: string,
  errors: ImportErrorItem[],
  onCloseCallback?: () => void,
) => {
  const errorListHtml = buildImportErrorListHtml(errors);

  Swal.fire({
    title: '',
    width: '650px',
    html: `
      <div style="text-align: center;">
        <div style="display: flex; justify-content: center; margin-top: 20px; margin-bottom: 20px;">
          <img style="height: 74px; width: 74px;" src="assets/img/info.png" alt="alert"/>
        </div>
        <div style="color: #3B3B3B; font-weight: 800; font-size: 20px; font-family: 'Poppins', sans-serif; margin-bottom: 20px;">${escapeHtml(message)}</div>
        <ul class="import-errors-scrollbar" style="color: #3B3B3B; font-family: 'Poppins', sans-serif; font-weight: 500; font-size: 15px; text-align: left; list-style-type: disc; list-style-position: outside; max-height: 180px; overflow-y: auto; width: 100%; padding-left: 25px; padding-right: 10px; margin-bottom: 20px; overscroll-behavior: contain;">
          ${errorListHtml}
        </ul>
        <div>
          <p style="font-size: 14px; color: #3B3B3B; font-family: 'Arial', sans-serif; margin-bottom: 0;">Tap anywhere to try again.</p>
        </div>
      </div>
    `,
    icon: undefined,
    showConfirmButton: false,
    animation: false,
    buttonsStyling: false,
    showCloseButton: false,
    allowOutsideClick: true,
    customClass: {
      popup: 'custom-popup-class',
      container: 'custom-container-class',
    },
    didOpen: (popup) => {
      const scrollbar = popup.querySelector(
        '.import-errors-scrollbar',
      ) as HTMLElement;
      if (scrollbar) {
        scrollbar.addEventListener('click', (e) => {
          e.stopPropagation();
        });

        scrollbar.addEventListener(
          'wheel',
          (e: WheelEvent) => {
            const scrollTop = scrollbar.scrollTop;
            const scrollHeight = scrollbar.scrollHeight;
            const clientHeight = scrollbar.clientHeight;
            const delta = e.deltaY;

            if (
              (delta < 0 && scrollTop <= 0) ||
              (delta > 0 && scrollTop + clientHeight >= scrollHeight)
            ) {
              e.preventDefault();
            }
          },
          { passive: false },
        );
      }
      popup.addEventListener('click', (e) => {
        const target = e.target as HTMLElement;
        if (target && !target.closest('.import-errors-scrollbar')) {
          Swal.clickConfirm();
        }
      });
    },
    didClose: onCloseCallback,
  });
};

const maliciousPattern =
  /(<script|<\/script>|javascript:|onerror=|onload=|onmouseover=)/gi;
const forbiddenCharsPattern = /[<>;?#%{}@^&*+=$()]/;

export {
  charValidate,
  CURRENT_DATE,
  CURRENT_TIME,
  currentYear,
  CustomField,
  forbiddenCharsPattern,
  formatCurrency,
  formatDateCommon,
  formatStringAmountToDecimal,
  formatTimeTZRange,
  formatWithOrdinal,
  getFileName,
  getFileNameFromUrl,
  getOrdinalSuffix,
  getResolution,
  getReturnUrl,
  getSubdomainOrDomain,
  isEmptyObject,
  maliciousPattern,
  noLeadingOrTrailingSpacesValidator,
  noWhitespaceValidator,
  parseDateWithoutTimezone,
  removeDuplicates,
  renderFontFamily,
  setVideoThumbnailImage,
  showAlertMessage,
  showImportErrorsAlertV2,
  socialUrlValidator,
  toCamelCase,
  trimSpacesFromCenter,
};

/** Registration links are built as /{type}?eventId=&orgId= — see EventService.qrUrl. */
export const CLASS_TYPE_ROUTES: Record<string, string> = {
  camp: 'camps',
  clinic: 'clinics',
  dailytraining: 'dailytraining',
  tryout: 'tryouts',
};
